/**
 * Secure Storage Utility
 * Provides encrypted localStorage wrapper to protect sensitive data from XSS attacks
 * Uses AES encryption with a client-side key
 */

import CryptoJS from 'crypto-js';

// Generate a unique key based on browser fingerprint
// Note: This is not perfect security but significantly better than plain localStorage
const getEncryptionKey = () => {
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    new Date().getTimezoneOffset(),
    screen.width + 'x' + screen.height
  ].join('|');
  
  // Use a combination of fingerprint and a static secret
  // In production, this could be enhanced with additional entropy
  return CryptoJS.SHA256(fingerprint + 'myiniraa-secure-key-2024').toString();
};

const ENCRYPTION_KEY = getEncryptionKey();

/**
 * Encrypt and store data in localStorage
 * @param {string} key - Storage key
 * @param {string} value - Value to encrypt and store
 */
export const setSecureItem = (key, value) => {
  try {
    const encrypted = CryptoJS.AES.encrypt(value, ENCRYPTION_KEY).toString();
    localStorage.setItem(key, encrypted);
  } catch (error) {
    // Fallback to regular storage if encryption fails
    localStorage.setItem(key, value);
  }
};

/**
 * Retrieve and decrypt data from localStorage
 * @param {string} key - Storage key
 * @returns {string|null} Decrypted value or null if not found
 */
export const getSecureItem = (key) => {
  try {
    const encrypted = localStorage.getItem(key);
    if (!encrypted) return null;
    
    const decrypted = CryptoJS.AES.decrypt(encrypted, ENCRYPTION_KEY);
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    // If decryption fails, try returning raw value (backward compatibility)
    return localStorage.getItem(key);
  }
};

/**
 * Remove item from localStorage
 * @param {string} key - Storage key
 */
export const removeSecureItem = (key) => {
  localStorage.removeItem(key);
};

/**
 * Clear all items from localStorage
 */
export const clearSecureStorage = () => {
  localStorage.clear();
};
